'use client'

import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fetchNavigationSnapshot } from '@/lib/navigation/navigationClient'
import type {
  GameweekInfo,
  NavigationGameweek,
  NavigationLiveMatch,
  NotificationData,
  TokenBalances,
  UserProfileData,
  UserRankData
} from '@/lib/navigation/navigationContract'

export type {
  GameweekInfo,
  NavigationLiveMatch,
  NotificationData,
  TokenBalances,
  UserProfileData,
  UserRankData
}

export interface UserAchievementsData {
  completed: number
  total: number
}

export interface NavigationContextData {
  availableGameweeks: NavigationGameweek[]
  tokenBalances: TokenBalances | null
  gameweekInfo: GameweekInfo | null
  notifications: NotificationData | null
  userRank: UserRankData | null
  liveMatches: NavigationLiveMatch[]
  userProfile: UserProfileData | null
  isLoading: boolean
  error: string | null
}

export function useAllNavigationData(): NavigationContextData {
  const { user } = useAuth()
  const query = useQuery({
    queryKey: ['navigationSnapshot', user?.id ?? null],
    queryFn: fetchNavigationSnapshot,
    enabled: !!user?.id,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 30 * 1000
  })

  return {
    availableGameweeks: query.data?.availableGameweeks ?? [],
    tokenBalances: query.data?.tokenBalances ?? null,
    gameweekInfo: query.data?.gameweekInfo ?? null,
    notifications: query.data?.notifications ?? null,
    userRank: query.data?.userRank ?? null,
    liveMatches: query.data?.liveMatches ?? [],
    userProfile: query.data?.userProfile ?? null,
    isLoading: query.isLoading,
    error: query.error?.message ?? null
  }
}
